import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Edit2, Trash2, Check, X, Shield, ShieldOff, Plus } from 'lucide-react';
import pb from '../pocketbase';

const AdminPage = () => {
    const [users, setUsers] = useState([]);
    const [discordMap, setDiscordMap] = useState({});
    const [visiblePwd, setVisiblePwd] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newUser, setNewUser] = useState({ username: '', password: '' });
    const [newUserPwdVisible, setNewUserPwdVisible] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    const fetchUsers = async () => {
        try {
            const records = await pb.collection('users').getFullList({ sort: 'username' });
            setUsers(records);
        } catch (err) {
            console.error('Erreur chargement users:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDiscordUsers = async () => {
        try {
            const records = await pb.collection('discord_users').getFullList();
            const map = {};
            records.forEach(r => { map[r.username] = r; });
            setDiscordMap(map);
        } catch (err) {
            console.error('Erreur chargement discord_users:', err);
        }
    };

    useEffect(() => { fetchUsers(); fetchDiscordUsers(); }, []);

    const togglePwd = (id) => setVisiblePwd(v => ({ ...v, [id]: !v[id] }));

    const startEdit = (user) => {
        setEditingId(user.id);
        setEditData({ username: user.username, rawPassword: user.rawPassword || '', isAdmin: user.isAdmin || false });
    };

    const cancelEdit = () => { setEditingId(null); setEditData({}); };

    const saveEdit = async (id) => {
        try {
            const updateData = {
                username: editData.username,
                isAdmin: editData.isAdmin,
                rawPassword: editData.rawPassword,
            };
            if (editData.rawPassword) {
                updateData.password = editData.rawPassword;
                updateData.passwordConfirm = editData.rawPassword;
            }
            await pb.collection('users').update(id, updateData);
            setEditingId(null);
            fetchUsers();
        } catch (err) {
            alert('Erreur lors de la sauvegarde : ' + (err.message || ''));
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Supprimer "${user.username}" ? Cette action est irréversible.`)) return;
        try {
            await pb.collection('users').delete(user.id);
            fetchUsers();
        } catch (err) {
            alert('Erreur lors de la suppression');
        }
    };

    const openCreateModal = () => {
        setNewUser({ username: '', password: '' });
        setNewUserPwdVisible(false);
        setCreateError('');
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        if (creating) return;
        setShowCreateModal(false);
    };

    const createUser = async () => {
        if (!newUser.username.trim() || !newUser.password) {
            setCreateError('Le nom d\'utilisateur et le mot de passe sont requis.');
            return;
        }
        setCreating(true);
        setCreateError('');
        try {
            await pb.collection('users').create({
                username: newUser.username.trim(),
                password: newUser.password,
                passwordConfirm: newUser.password,
                rawPassword: newUser.password,
                isAdmin: false,
            });
            setShowCreateModal(false);
            fetchUsers();
        } catch (err) {
            setCreateError(err?.message || 'Erreur lors de la création de l\'utilisateur.');
        } finally {
            setCreating(false);
        }
    };

    if (loading) return <div style={{ padding: '40px', color: 'white' }}>Chargement...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.headerRow} className="fade-in-up">
                <h1 style={styles.title} className="tvs-title-gradient">🛠️ Gestion des utilisateurs</h1>
                <button style={styles.btnNew} className="tvs-btn-lift" onClick={openCreateModal}>
                    <Plus size={16} /> Nouveau
                </button>
            </div>
            <div style={styles.tableWrapper} className="fade-in-up">
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Utilisateur</th>
                            <th style={styles.th}>Discord</th>
                            <th style={styles.th}>Mot de passe</th>
                            <th style={styles.th}>Admin</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {
                            const isEditing = editingId === user.id;
                            return (
                                <tr key={user.id} style={styles.tr} className="tvs-row-hover">
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <input
                                                style={styles.input}
                                                value={editData.username}
                                                onChange={e => setEditData({ ...editData, username: e.target.value })}
                                            />
                                        ) : (
                                            <span style={{ color: 'white', fontWeight: 500 }}>{user.username}</span>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        <DiscordCell record={discordMap[user.username]} />
                                    </td>
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <input
                                                style={styles.input}
                                                value={editData.rawPassword}
                                                onChange={e => setEditData({ ...editData, rawPassword: e.target.value })}
                                                placeholder="Nouveau mot de passe"
                                            />
                                        ) : (
                                            <div style={styles.pwdCell}>
                                                <span style={styles.pwdText}>
                                                    {visiblePwd[user.id] ? (user.rawPassword || '—') : '••••••••'}
                                                </span>
                                                <button
                                                    style={styles.iconBtn}
                                                    className="tvs-btn-lift"
                                                    onClick={() => togglePwd(user.id)}
                                                    title={visiblePwd[user.id] ? 'Masquer' : 'Afficher'}
                                                >
                                                    {visiblePwd[user.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <input
                                                type="checkbox"
                                                checked={editData.isAdmin}
                                                onChange={e => setEditData({ ...editData, isAdmin: e.target.checked })}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                            />
                                        ) : (
                                            user.isAdmin
                                                ? <Shield size={18} color="#ff4444" />
                                                : <ShieldOff size={18} color="#555" />
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.actions}>
                                            {isEditing ? (
                                                <>
                                                    <button style={styles.btnGreen} className="tvs-btn-lift" onClick={() => saveEdit(user.id)} title="Sauvegarder">
                                                        <Check size={15} />
                                                    </button>
                                                    <button style={styles.btnGray} className="tvs-btn-lift" onClick={cancelEdit} title="Annuler">
                                                        <X size={15} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button style={styles.btnGray} className="tvs-btn-lift" onClick={() => startEdit(user)} title="Modifier">
                                                        <Edit2 size={15} />
                                                    </button>
                                                    <button style={styles.btnRed} className="tvs-btn-lift" onClick={() => deleteUser(user)} title="Supprimer">
                                                        <Trash2 size={15} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div style={styles.modalOverlay} onClick={closeCreateModal}>
                    <div style={styles.modalBox} className="fade-in-up" onClick={e => e.stopPropagation()}>
                        <h2 style={styles.modalTitle} className="tvs-title-gradient">Nouvel utilisateur</h2>

                        <label style={styles.modalLabel}>Nom d'utilisateur</label>
                        <input
                            style={styles.input}
                            className="tvs-input"
                            value={newUser.username}
                            onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                            placeholder="username"
                            autoFocus
                        />

                        <label style={styles.modalLabel}>Mot de passe</label>
                        <div style={styles.pwdCell}>
                            <input
                                style={styles.input}
                                className="tvs-input"
                                type={newUserPwdVisible ? 'text' : 'password'}
                                value={newUser.password}
                                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                placeholder="mot de passe"
                                onKeyDown={e => { if (e.key === 'Enter') createUser(); }}
                            />
                            <button
                                style={styles.iconBtn}
                                className="tvs-btn-lift"
                                onClick={() => setNewUserPwdVisible(v => !v)}
                                title={newUserPwdVisible ? 'Masquer' : 'Afficher'}
                                type="button"
                            >
                                {newUserPwdVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>

                        {createError && <div style={styles.modalError}>{createError}</div>}

                        <div style={styles.modalActions}>
                            <button style={styles.btnGray} className="tvs-btn-lift" onClick={closeCreateModal} disabled={creating}>
                                Annuler
                            </button>
                            <button style={styles.btnGreen} className="tvs-btn-lift" onClick={createUser} disabled={creating}>
                                {creating ? 'Création...' : 'Créer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const DiscordCell = ({ record }) => {
    if (!record) {
        return <span style={{ color: '#444', fontSize: '0.8rem' }}>—</span>;
    }
    const label = record.discord_username || record.discord_id;
    const tooltip = record.discord_mention;
    return (
        <span
            title={tooltip}
            style={{
                color: '#7289da',
                fontSize: '0.9rem',
                cursor: 'default',
                borderBottom: '1px dotted #7289da',
                whiteSpace: 'nowrap'
            }}
        >
            {label}
        </span>
    );
};

const styles = {
    container: { padding: '30px', maxWidth: '900px', margin: '0 auto', color: 'white' },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
    title: { color: 'white', margin: 0 },
    btnNew: { background: '#166534', border: 'none', color: 'white', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', fontWeight: 500 },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalBox: { background: '#161616', border: '1px solid #333', borderRadius: '18px', padding: '28px', width: '360px', maxWidth: '90vw', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' },
    modalTitle: { color: 'white', margin: '0 0 20px 0', fontSize: '1.2rem' },
    modalLabel: { display: 'block', color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', marginTop: '14px' },
    modalError: { color: '#f87171', fontSize: '0.85rem', marginTop: '14px' },
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' },
    tableWrapper: { overflowX: 'auto', backgroundColor: '#161616', borderRadius: '16px', border: '1px solid #333' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '14px 16px', textAlign: 'left', color: '#888', borderBottom: '1px solid #333', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
    td: { padding: '12px 16px', borderBottom: '1px solid #222', verticalAlign: 'middle' },
    tr: { transition: 'background 0.15s' },
    pwdCell: { display: 'flex', alignItems: 'center', gap: '8px' },
    pwdText: { fontFamily: 'monospace', color: '#ccc', minWidth: '80px' },
    actions: { display: 'flex', gap: '8px' },
    input: { background: '#222', border: '1px solid #444', color: 'white', padding: '6px 10px', borderRadius: '5px', fontSize: '0.9rem', outline: 'none', width: '100%' },
    iconBtn: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' },
    btnGray: { background: '#333', border: 'none', color: 'white', padding: '6px 10px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    btnGreen: { background: '#166534', border: 'none', color: 'white', padding: '6px 10px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    btnRed: { background: '#7f1d1d', border: 'none', color: 'white', padding: '6px 10px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
};

export default AdminPage;
